import React, { useState, useEffect } from 'react';
import { Menu, X, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Se não estiver na landing page, navegar primeiro
    if (!isLandingPage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      setIsMobileMenuOpen(false);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
      // Announce navigation for screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = `Navegando para seção ${element.getAttribute('aria-label') || sectionId}`;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    }
  };

  const handleAdminClick = () => {
    if (user && user.role === 'admin') {
      navigate('/admin/partners');
    } else {
      navigate('/admin/login');
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { label: t('header.nav.home'), id: 'hero' },
    { label: t('header.nav.mission'), id: 'mission' },
    { label: t('header.nav.services'), id: 'services' },
    { label: t('header.nav.events'), id: 'events' },
    { label: t('header.nav.news'), id: 'news' },
    { label: t('header.nav.about'), id: 'about' },
    { label: t('header.nav.partners'), id: 'partners', scroll: true },
    { label: t('header.nav.contact'), id: 'contact' }
  ];

  // WhatsApp link
  const whatsappLink = `https://wa.me/5517997833002?text=${encodeURIComponent('Olá! Gostaria de falar com um especialista sobre inclusão e acessibilidade.')}`;

  return (
    <header
      role="banner"
      aria-label="Cabeçalho principal do site"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      {/* Skip to main content link for screen readers */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-teal-600 focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        {t('header.skipToContent')}
      </a>

      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div 
            className="flex items-center space-x-3 cursor-pointer" 
            onClick={() => scrollToSection('hero')}
            role="button"
            tabIndex={0}
            aria-label={t('header.logo')}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                scrollToSection('hero');
              }
            }}
          >
            <img 
              src="/logo-chico.png" 
              alt="Logo Chico da Inclusão - Símbolo de acessibilidade com as cores azul e verde água" 
              className="h-14 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-900">Chico da Inclusão</h1>
              <p className="text-xs text-gray-600">{t('header.subtitle')}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center space-x-6"
            role="navigation"
            aria-label="Navegação principal"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-teal-600 transition-colors duration-200 font-medium"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={handleAdminClick}
              className="flex items-center gap-1 text-gray-700 hover:text-teal-600 transition-colors duration-200 font-medium"
              title={user && user.role === 'admin' ? 'Dashboard Admin' : 'Login Admin'}
            >
              <Shield size={16} />
              Admin
            </button>
          </nav>

          {/* CTA and Language Switcher */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSwitcher />
            <Button
              onClick={() => window.open(whatsappLink, '_blank')}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {t('header.cta')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-teal-600"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X size={24} aria-hidden="true" />
            ) : (
              <Menu size={24} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            id="mobile-menu"
            className="lg:hidden py-4 border-t border-gray-200"
            role="navigation"
            aria-label="Menu de navegação mobile"
          >
            <nav className="flex flex-col space-y-4">
              <div className="pb-4 border-b border-gray-200">
                <LanguageSwitcher />
              </div>
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-gray-700 hover:text-teal-600 transition-colors duration-200 font-medium py-2"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={handleAdminClick}
                className="flex items-center gap-2 text-left text-gray-700 hover:text-teal-600 transition-colors duration-200 font-medium py-2"
              >
                <Shield size={16} />
                Admin {user && user.role === 'admin' && '(Dashboard)'}
              </button>
              <Button
                onClick={() => window.open(whatsappLink, '_blank')}
                className="bg-teal-600 hover:bg-teal-700 text-white w-full"
              >
                {t('header.cta')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
